import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import theme from 'themes/default.theme';
import GlobalStyles from 'themes/global.style';
import AuthProvider from 'context/AuthProvider';
import AppRoutes from './router';
import { ConfigProvider } from 'antd';
import 'antd/dist/reset.css';
import './index.css';
import FireAuth from 'context/FireAuth';
import { store, persistor } from './store/store';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { COMETCHAT_CONFIG } from './config/cometChatConfig';
import {
  UIKitSettingsBuilder,
  CometChatUIKit,
} from '@cometchat/chat-uikit-react';
import { setupLocalization } from 'containers/CometChat/utils/utils';
import { CometChatProvider } from 'containers/CometChat/context/CometChatContext';
import {
  initFcmAndGetToken,
  attachForegroundMessageHandler,
} from 'library/helpers/fcm';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const getCometChatConstants = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isQA =
      hostname.includes('qa') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1';

    if (isQA) {
      return {
        APP_ID: '168305471b5c86235',
        REGION: 'in',
        AUTH_KEY: 'd5295d6af5bc5af1bc67de00c330098014961608',
      };
    }

    return {
      APP_ID: '1683056a57e16ed43',
      REGION: 'in',
      AUTH_KEY: '09069f6dde13990f45d60d7e79a7741dc2119e3f',
    };
  }

  return {
    APP_ID: '168305471b5c86235',
    REGION: 'in',
    AUTH_KEY: 'd5295d6af5bc5af1bc67de00c330098014961608',
  };
};

export const COMETCHAT_CONSTANTS = getCometChatConstants();

const AppWrapper: React.FC = () => {
  const [isCometChatInitialized, setIsCometChatInitialized] = useState(false);
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration: ServiceWorkerRegistration) => {
          console.log('ServiceWorker registered: ', registration);
        })
        .catch((error: Error) => {
          console.error('ServiceWorker registration failed: ', error);
        });
    }

    // Initialize FCM on app boot (permission prompt at most once)
    initFcmAndGetToken().catch(() => {});
    // Attach foreground handler to surface push when app is open
    attachForegroundMessageHandler().catch(() => {});

    // CometChat Initialization
    const initializeCometChat = async () => {
      let appID = COMETCHAT_CONFIG.APP_ID;
      let region = COMETCHAT_CONFIG.REGION;
      let appSetting = new CometChat.AppSettingsBuilder()
        .subscribePresenceForAllUsers()
        .setRegion(region)
        .autoEstablishSocketConnection(true)
        .build();

      CometChat.init(appID, appSetting).then(
        () => {
          console.log('Initialization completed successfully');
          setIsCometChatInitialized(true);
          try {
            const userDetails = JSON.parse(
              localStorage.getItem('userDetails') || 'null',
            );
            const uidFromStorage = userDetails && userDetails.id;
            if (uidFromStorage) {
              loginDummyUser();
            } else {
              console.log('No Unpaqd userDetails found; skipping CometChat login');
            }
          } catch (e) {
            console.log('Skipping CometChat login due to missing userDetails');
          }
        },
        (error) => {
          console.log('Initialization failed with error:', error);
        },
      );
    };

    const loginDummyUser = () => {
      const userDetails = JSON.parse(
        localStorage.getItem('userDetails') || 'null',
      );
      if (!userDetails || !userDetails.id) {
        return;
      }
      const UID = userDetails.id.toString();
      const name = userDetails.firstName
        ? `${userDetails.firstName} ${userDetails.lastName || ''}`
        : UID;
      console.log('Logging into CometChat with Unpaqd UID:', UID);

      var authKey = COMETCHAT_CONFIG.AUTH_KEY;

      // Helper to do the login
      const doLogin = () => {
        CometChat.getLoggedinUser().then(
          (user) => {
            if (!user) {
              CometChat.login(UID, authKey).then(
                (user) => {
                  console.log('Login Successful:', { user });
                },
                (error) => {
                  console.log('Login failed with exception:', { error });
                },
              );
            } else {
              console.log('User already logged in:', { user });
            }
          },
          (error) => {
            console.log('Some Error Occurred', { error });
          },
        );
      };

      const cometUser = new CometChat.User(UID);
      cometUser.setName(name);

      CometChat.createUser(cometUser, authKey)
        .then(() => {
          console.log('User created in CometChat!');
          doLogin();
        })
        .catch((error) => {
          if (error.code === 'ERR_UID_ALREADY_EXISTS') {
            // User exists, just login
            doLogin();
          } else {
            console.error('Failed to create user in CometChat:', error);
          }
        });
    };

    initializeCometChat();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={{ hashed: false } as any}>
        <React.Fragment>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              {' '}
              {/* <-- Wrap App in PersistGate */}
              <GlobalStyles />
              <BrowserRouter>
                <FireAuth>
                  <AuthProvider>
                    <CometChatProvider>
                      <AppRoutes />
                      <ToastContainer
                        position="top-right"
                        autoClose={4000}
                        hideProgressBar={false}
                        newestOnTop
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="light"
                      />
                    </CometChatProvider>
                  </AuthProvider>
                </FireAuth>
              </BrowserRouter>
            </PersistGate>
          </Provider>
        </React.Fragment>
      </ConfigProvider>
    </ThemeProvider>
  );
};

// Always render the app first
const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');
const root = createRoot(rootElement);
root.render(<AppWrapper />);

// Initialize CometChat separately
const uiKitSettings = new UIKitSettingsBuilder()
  .setAppId(COMETCHAT_CONSTANTS.APP_ID)
  .setRegion(COMETCHAT_CONSTANTS.REGION)
  .setAuthKey(COMETCHAT_CONSTANTS.AUTH_KEY)
  .subscribePresenceForAllUsers()
  .build();

CometChatUIKit.init(uiKitSettings)
  ?.then(() => {
    setupLocalization();
    console.log('CometChat initialized successfully');

    // Retrieve UID from localStorage userDetails object
    let UID = '';
    const userDetailsString = localStorage.getItem('userDetails');
    if (userDetailsString) {
      try {
        const userDetails = JSON.parse(userDetailsString);
        UID = userDetails?.id || '';
      } catch (e) {
        console.error('Failed to parse userDetails from localStorage:', e);
      }
    }

    // Only attempt login if we have a UID
    if (UID) {
      CometChatUIKit.getLoggedinUser().then(async (user: CometChat.User | null) => {
        if (!user) {
          // Ensure user exists in CometChat before logging in
          try {
            let userName = UID;
            try {
              const raw = localStorage.getItem('userDetails');
              if (raw) {
                const parsed = JSON.parse(raw);
                userName = [parsed?.firstName, parsed?.lastName].filter(Boolean).join(' ').trim() || UID;
              }
            } catch (_) {}
            const cometUser = new CometChat.User(UID);
            cometUser.setName(userName);
            await CometChat.createUser(cometUser, COMETCHAT_CONFIG.AUTH_KEY);
          } catch (createErr: any) {
            if (createErr?.code !== 'ERR_UID_ALREADY_EXISTS') {
              console.warn('CometChat createUser error on page load:', createErr);
            }
          }
          CometChatUIKit.login(UID)
            .then((loggedUser: CometChat.User) => {
              console.log('Login Successful:', loggedUser);
            })
            .catch((error) => console.error('Login Failed:', error));
        } else {
          console.log('User already logged in:', user);
        }
      });
    } else {
      console.log('No UID found in localStorage, skipping CometChat login');
    }
  })
  .catch((error) => {
    console.error('CometChat initialization failed:', error);
  });
